import { useEffect, useState } from "react";
import CassettePlayer from "@/components/CassettePlayer";
import { Globe, MessageCircle, Heart, Share2 } from "lucide-react";
import { toast } from "sonner";

const WHATSAPP_NUMBER = "393917041816"; // +39 391 704 1816, formato internazionale senza '+'
const WHATSAPP_TEXT   = encodeURIComponent("Ciao Radio Rumore Zero! Vi scrivo da Rumore Zero PWA.");

async function shareApp() {
  const url = (typeof window !== "undefined" && window.location?.origin) || "https://www.rumorezero.com";
  const shareData = {
    title: "Radio Rumore Zero",
    text: "Ascolta Radio Rumore Zero! I grandi successi degli anni 60·70·80·90 in diretta 24/7.",
    url,
  };
  try {
    if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
      await navigator.share(shareData);
      return;
    }
  } catch (err) {
    if (err && err.name === "AbortError") return;
  }
  // Fallback: copy link to clipboard
  try {
    await navigator.clipboard.writeText(`${shareData.text} ${url}`);
    toast.success("Link copiato! Incollalo nella chat con il tuo amico.");
  } catch {
    // last-ditch fallback: open WhatsApp with text
    const wa = `https://wa.me/?text=${encodeURIComponent(`${shareData.text} ${url}`)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  }
}

const ACTION_ITEMS = [
  {
    id: "share",
    label: "Condividi ad un amico",
    sub: "Whatsapp · Telegram · …",
    icon: Share2,
    color: "azure", // blue button
    onClick: shareApp,
  },
  {
    id: "website",
    label: "Sito ufficiale",
    sub: "rumorezero.com",
    href: "https://www.rumorezero.com",
    icon: Globe,
  },
  {
    id: "whatsapp",
    label: "Scrivici su WhatsApp",
    sub: "+39 391 704 1816",
    href: `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_TEXT}`,
    icon: MessageCircle,
  },
  {
    id: "donate",
    label: "Sostieni la radio",
    sub: "Donazione PayPal",
    href: "https://www.paypal.com/donate/?business=Z8GL3NH8T78RL&no_recurring=0&item_name=Sostieni+Radio+Madre+Terra&currency_code=EUR",
    icon: Heart,
    color: "accent",
  },
];

export default function Hero() {
  const [liveListeners, setLiveListeners] = useState(null);
  const [tracksToday, setTracksToday] = useState(0);

  // Count today's history entries (from localStorage) and keep it fresh
  useEffect(() => {
    const countToday = () => {
      try {
        const raw = localStorage.getItem("rrz-track-history");
        if (!raw) return 0;
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return 0;
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        const t0 = startOfDay.getTime();
        return arr.filter((h) => (h?.ts || 0) >= t0).length;
      } catch { return 0; }
    };
    setTracksToday(countToday());
    const onNP = () => setTracksToday(countToday());
    window.addEventListener("rrz:now-playing", onNP);
    return () => window.removeEventListener("rrz:now-playing", onNP);
  }, []);

  useEffect(() => {
    const onNP = (ev) => {
      const n = ev?.detail?.listeners;
      if (typeof n === "number" && n >= 0) setLiveListeners(n);
    };
    window.addEventListener("rrz:now-playing", onNP);
    return () => window.removeEventListener("rrz:now-playing", onNP);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero"
      className="relative overflow-hidden border-b border-[#332f2b]"
    >
      {/* background glow */}
      <div className="absolute inset-0 -z-0">
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#FF6B00]/10 blur-[120px]" />
        <div className="absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-[#E1A140]/10 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/4624/vintage-radios.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(100%) contrast(1.1)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-8 sm:pt-14 pb-12 sm:pb-20">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 sm:gap-10 lg:gap-12 items-center">
          {/* left text column (shown second on mobile, first on desktop) */}
          <div className="order-2 lg:order-1 min-w-0">
            <div data-testid="hero-eyebrow" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF6B00]/30 bg-[#FF6B00]/5 text-[#FF6B00] font-mono text-[10px] uppercase tracking-[0.3em] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00] led-on" />
              In diretta · 24/7
            </div>
            <h1
              data-testid="hero-title"
              className="font-anton uppercase leading-[0.95] text-[44px] sm:text-7xl lg:text-[88px] text-[#F5F2EB] break-words"
            >
              Quattro <br />
              <span className="text-[#FF6B00] drop-shadow-[0_0_20px_rgba(255,107,0,0.4)]">
                decadi
              </span>{" "}
              di <br />
              grandi <br />
              successi.
            </h1>
            <p
              data-testid="hero-sub"
              className="mt-6 font-mono text-sm sm:text-base text-[#A39A8F] max-w-md leading-relaxed"
            >
              Radio Rumore Zero trasmette esclusivamente le hit immortali degli
              anni <span className="text-[#E1A140]">60</span>,{" "}
              <span className="text-[#E1A140]">70</span>,{" "}
              <span className="text-[#E1A140]">80</span> e{" "}
              <span className="text-[#E1A140]">90</span>. Nessuna pubblicità.
              Solo musica.
            </p>

            <div className="mt-7 flex items-center flex-wrap gap-x-5 gap-y-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#69635c]">
              <div data-testid="stat-tracks" className="flex items-center gap-2">
                <span className="text-[#FF6B00] text-lg font-anton">12K+</span>
                Brani
              </div>
              <span className="w-px h-5 bg-[#332f2b]" />
              <div data-testid="stat-live" className="flex items-center gap-2">
                <span className="text-[#FF6B00] text-lg font-anton">24/7</span>
                Diretta
              </div>
              <span className="w-px h-5 bg-[#332f2b]" />
              <div data-testid="stat-ads" className="flex items-center gap-2">
                <span className="text-[#FF6B00] text-lg font-anton">0</span>
                Pubblicità
              </div>
              {liveListeners != null && (
                <>
                  <span className="w-px h-5 bg-[#332f2b]" />
                  <div data-testid="stat-listeners" className="flex items-center gap-2">
                    <span className="relative flex w-2 h-2">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-[#FF6B00] opacity-75 animate-ping" />
                      <span className="relative inline-flex w-2 h-2 rounded-full bg-[#FF6B00]" />
                    </span>
                    <span className="text-[#FF6B00] text-lg font-anton tabular-nums">{liveListeners}</span>
                    In ascolto
                  </div>
                </>
              )}
              {tracksToday > 0 && (
                <>
                  <span className="w-px h-5 bg-[#332f2b]" />
                  <div data-testid="stat-tracks-today" className="flex items-center gap-2">
                    <span className="text-[#E1A140] text-lg font-anton tabular-nums">{tracksToday}</span>
                    Oggi per te
                  </div>
                </>
              )}
            </div>
          </div>

          {/* cassette player + action buttons (shown first on mobile) */}
          <div className="order-1 lg:order-2 w-full min-w-0">
            <CassettePlayer />

            <div
              data-testid="player-action-buttons"
              className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3 max-w-2xl mx-auto"
            >
              {ACTION_ITEMS.map(({ id, label, sub, href, icon: Icon, color, onClick }) => {
                const isAccent = color === "accent";
                const isAzure  = color === "azure";

                const baseClass = "group flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 text-left w-full";
                const variantClass = isAccent
                  ? "bg-[#FF6B00] text-[#12100e] border-[#FF6B00] hover:bg-[#ff8226] shadow-[0_0_20px_rgba(255,107,0,0.35)]"
                  : isAzure
                    ? "bg-[#1E88E5] text-white border-[#1E88E5] hover:bg-[#2196f3] shadow-[0_0_20px_rgba(30,136,229,0.45)]"
                    : "bg-[#1a1816] text-[#F5F2EB] border-[#332f2b] hover:border-[#FF6B00] hover:text-[#FF6B00]";

                const iconWrapClass = isAccent
                  ? "bg-[#12100e]/15"
                  : isAzure
                    ? "bg-white/15"
                    : "bg-[#12100e] border border-[#332f2b] group-hover:border-[#FF6B00]/60";

                const subClass = isAccent
                  ? "text-[#12100e]/70"
                  : isAzure
                    ? "text-white/75"
                    : "text-[#69635c] group-hover:text-[#A39A8F]";

                const content = (
                  <>
                    <span className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${iconWrapClass}`}>
                      <Icon size={18} />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-oswald uppercase text-sm leading-tight truncate">
                        {label}
                      </span>
                      <span className={`block font-mono text-[10px] uppercase tracking-[0.18em] truncate ${subClass}`}>
                        {sub}
                      </span>
                    </span>
                  </>
                );

                return onClick ? (
                  <button
                    key={id}
                    type="button"
                    data-testid={`action-${id}`}
                    onClick={onClick}
                    className={`${baseClass} ${variantClass}`}
                  >
                    {content}
                  </button>
                ) : (
                  <a
                    key={id}
                    data-testid={`action-${id}`}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${baseClass} ${variantClass}`}
                  >
                    {content}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
